import { useEffect } from 'react';
import React from 'react';

import { Info } from 'lucide-react';
import { useParams } from 'react-router';
import { parseAsString, useQueryState } from 'nuqs';

import { cn } from '../../lib/utils';
import { useGetAssignmentDescription } from '../../query/assignment';
import { parseEditor } from '../answer-sheet';
import AssignmentStep from '../assignment-step';
import { Card } from '../ui/card';

const DESCRIPTION = {
  AFTER_FINAL_SUBMISSION:
    '각 문제별로 수정 횟수를 모두 사용했거나 만점을 받은 경우, 또는 마감 기한이 종료된 이후 해설을 확인할 수 있습니다.',
  AFTER_EACH_SUBMISSION: '각 문제를 제출하면 해당 문제의 해설을 확인할 수 있습니다.',
  AFTER_DEADLINE: '마감 기한이 종료된 이후 해설을 확인할 수 있습니다.',
} as const;

const Description = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();

  const [problemId, setProblemId] = useQueryState('problemId', parseAsString.withDefault(''));
  const { data: assignmentDescription, isLoading } = useGetAssignmentDescription(assignmentId!);

  const problem = assignmentDescription?.data?.problems.find(problem => problem.id === problemId);
  const problemIndex = assignmentDescription?.data?.problems.findIndex(
    problem => problem.id === problemId,
  );

  const isExplanation = problem?.isExplanation;

  useEffect(() => {
    if (!assignmentDescription?.data?.problems?.length) return;
    if (problemId) return;
    setProblemId(assignmentDescription.data.problems[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentDescription?.data?.problems]);

  if (isLoading) {
    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
          <div className="flex items-center gap-2 text-blue-800">
            <Info className="h-4 w-4" />
            <span className="text-sm font-medium">해설 정보를 불러오는 중...</span>
          </div>
        </div>
        <Card className="flex p-4">
          <div className="w-full flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <div className="text-gray-600">데이터를 불러오는 중...</div>
            </div>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
        <div className="flex items-center gap-2 text-blue-800">
          <Info className="h-4 w-4" />
          <span className="text-sm font-medium">
            해설 정보:
            {assignmentDescription?.data?.explanationDisplayStrategy === 'AFTER_FINAL_SUBMISSION' &&
              DESCRIPTION['AFTER_FINAL_SUBMISSION']}
            {assignmentDescription?.data?.explanationDisplayStrategy === 'AFTER_EACH_SUBMISSION' &&
              DESCRIPTION['AFTER_EACH_SUBMISSION']}
            {assignmentDescription?.data?.explanationDisplayStrategy === 'AFTER_DEADLINE' &&
              DESCRIPTION['AFTER_DEADLINE']}
          </span>
        </div>
      </div>
      <Card className="flex flex-col p-4 overflow-x-auto">
        <AssignmentStep
          problemId={problemId}
          setProblemId={setProblemId}
          problems={assignmentDescription?.data?.problems ?? []}
        />
        <div className="w-[1062px] max-w-full mx-auto">
          <div className="flex flex-col gap-4">
            <div className="text-lg font-bold">문제 {(problemIndex ?? 0) + 1}</div>
            {problem && (
              <div>
                <div className="flex flex-col gap-4 items-center w-full">
                  <p className="text-base font-bold text-left w-full">공통 영역</p>
                  <div
                    className="border rounded-lg shadow w-full p-[10px]"
                    dangerouslySetInnerHTML={{
                      __html: problem.problemHTML,
                    }}
                  />
                </div>
              </div>
            )}
            {problem?.questions.map((question, questionIndex) => (
              <div key={question.id} className="flex flex-col gap-4 border-b pb-4 last:border-b-0">
                <div className="text-lg font-bold">{questionIndex + 1}번 질문</div>

                <div className="flex flex-col gap-4 items-center w-full">
                  <p className="text-base font-bold flex text-left w-full">질문 영역</p>
                  <div className="border rounded-lg shadow p-[10px] w-full">
                    {question.questionHTML ? (
                      parseEditor(question.questionHTML)
                    ) : (
                      <div className="text-gray-500 text-center py-4">질문 영역이 없습니다.</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4 items-center w-full pointer-events-none">
                  <p className="text-base font-bold flex text-left w-full">정답 영역</p>

                  {!isExplanation ? (
                    <div className="border rounded-lg shadow p-[10px] w-full flex flex-col items-center">
                      <div className="w-full text-center text-sm py-8">
                        {
                          DESCRIPTION[
                            assignmentDescription?.data
                              ?.explanationDisplayStrategy as keyof typeof DESCRIPTION
                          ]
                        }
                      </div>
                    </div>
                  ) : question.questionType === 'SELECT' ? (
                    <div className="w-full flex flex-col gap-4 py-4 border rounded-lg shadow p-[10px]">
                      {(question?.choices || []).map((choice, index) => {
                        const isCorrect = choice.isAnswer;

                        return (
                          <React.Fragment key={index}>
                            <div className="flex gap-4 px-4">
                              <input
                                type="radio"
                                id={`correct-${choice.id}.${index}`}
                                name={`correct-question-${question.id}`}
                                checked={isCorrect || false}
                                readOnly
                              />
                              <label
                                htmlFor={`correct-${choice.id}.${index}`}
                                className={isCorrect ? 'font-bold text-blue-600' : ''}
                              >
                                {index + 1} 번 보기 {isCorrect && '(정답)'}
                              </label>
                            </div>
                            <div
                              className={cn(
                                'border rounded-lg shadow p-[10px] w-full flex flex-col items-center',
                                isCorrect && 'bg-blue-50 border-blue-200',
                              )}
                            >
                              <div
                                className="w-full py-2 answer"
                                dangerouslySetInnerHTML={{
                                  __html: choice.answerHTML,
                                }}
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="border rounded-lg shadow p-[10px] w-full flex flex-col items-center">
                      <div className={cn('w-full py-2 pointer-events-none')}>
                        {parseEditor(question.answerRawHTML ?? '')}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4 items-center w-full pointer-events-none">
                  <p className="text-base font-bold flex text-left w-full">해설 영역</p>
                  <div className="border rounded-lg shadow p-[10px] w-full flex flex-col items-center">
                    {!isExplanation ? (
                      <div className="w-full text-center text-sm py-8">
                        {
                          DESCRIPTION[
                            assignmentDescription?.data
                              ?.explanationDisplayStrategy as keyof typeof DESCRIPTION
                          ]
                        }
                      </div>
                    ) : (
                      <div className={cn('w-full py-2 pointer-events-none')}>
                        {parseEditor(question.explanationHTML ?? '')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </>
  );
};

export default Description;
